import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ChevronDown, ChevronRight, User as UserIcon, TrendingUp, Stethoscope,
  Briefcase, Check, X as XIcon, AlertTriangle, Sparkles, RefreshCw,
} from 'lucide-react';

/* ──────────────────────────────────────────────────────────────────────────
 * ContactFieldsPanel
 *
 * Renders the 4 categories of expanded CRM fields below the Contact Info card:
 *   1. Identity additions   (passport, companion travel)
 *   2. Sales Intelligence   (lead temperature, intent score, financial readiness, …)
 *   3. Medical Intelligence (condition category/subcategory, severity, prior treatments, …)
 *   4. Operational          (concierge, clinic location, payment, document status, …)
 *
 * Auto-saves via window.zignagenixAPI.patchContactFields(contactId, { col: val }).
 * Each edit is one IPC call — small, fast, atomic.
 *
 * Loads condition taxonomy + concierges once on mount.
 * ────────────────────────────────────────────────────────────────────────── */

const SECTION_PALETTE = {
  identity:    { color: '#0EA5E9', bg: '#E0F2FE', icon: UserIcon,    label: 'Identity' },
  sales:       { color: '#A855F7', bg: '#F3E8FF', icon: TrendingUp,  label: 'Sales Intelligence' },
  medical:     { color: '#10B981', bg: '#D1FAE5', icon: Stethoscope, label: 'Medical Intelligence' },
  operational: { color: '#F59E0B', bg: '#FEF3C7', icon: Briefcase,   label: 'Operational' },
};

/* ─── Enum option lists ──────────────────────────────────────────────────── */

const PASSPORT_STATUS = [
  ['valid', 'Valid'], ['expired', 'Expired'], ['pending', 'Pending'], ['none', 'None'],
];
const LEAD_TEMP = [['hot', '🔥 Hot'], ['warm', '☀️ Warm'], ['cold', '❄️ Cold']];
const FINANCIAL_READINESS = [
  ['cash_ready', 'Cash Ready'], ['financing_needed', 'Financing Needed'],
  ['shopping_around', 'Shopping Around'], ['unknown', 'Unknown'],
];
const COMM_PREF = [
  ['call', 'Call'], ['text', 'Text'], ['email', 'Email'], ['whatsapp', 'WhatsApp'],
];
const LAST_OUTCOME = [
  ['no_answer', 'No Answer'], ['interested', 'Interested'],
  ['requested_pricing', 'Requested Pricing'], ['needs_financing', 'Needs Financing'],
  ['wants_call_back', 'Wants Call Back'], ['booked_consult', 'Booked Consult'],
  ['not_interested', 'Not Interested'],
];
const FOLLOWUP_PRIORITY = [
  ['urgent', 'Urgent'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low'],
];
const DECISION_MAKER = [
  ['self', 'Self'], ['spouse_involved', 'Spouse Involved'],
  ['family_involved', 'Family Involved'], ['needs_approval', 'Needs Approval'],
];
const MEDICAL_URGENCY = [
  ['severe_pain', 'Severe Pain'], ['mobility_declining', 'Mobility Declining'],
  ['cosmetic', 'Cosmetic'], ['longevity', 'Longevity'], ['performance', 'Performance'],
];
const CONFIDENCE = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High']];
const SEVERITY = [['mild', 'Mild'], ['moderate', 'Moderate'], ['severe', 'Severe']];
const MOBILITY = [
  ['normal', 'Normal'], ['limited', 'Limited'],
  ['wheelchair', 'Wheelchair'], ['assisted_walking', 'Assisted Walking'],
];
const CANDIDATE = [
  ['excellent', 'Excellent Candidate'], ['possible', 'Possible Candidate'],
  ['not_recommended', 'Not Recommended'],
];
const CLINIC = [
  ['tijuana', 'Tijuana'], ['cancun', 'Cancún'], ['puerto_vallarta', 'Puerto Vallarta'],
];
const PAYMENT = [
  ['no_payment', 'No Payment'], ['deposit_paid', 'Deposit Paid'],
  ['partial_paid', 'Partial Paid'], ['paid_in_full', 'Paid in Full'],
  ['financing_pending', 'Financing Pending'],
];
const NO_SHOW_RISK = [['low', 'Low'], ['medium', 'Medium'], ['high', 'High']];

const COMPETITORS = [
  ['r3', 'R3'], ['cpi', 'CPI'], ['dreambody', 'DreamBody'],
  ['panama', 'Panama'], ['colombia', 'Colombia'], ['other', 'Other'],
];
const PRIOR_TREATMENTS = [
  ['prp', 'PRP'], ['surgery', 'Surgery'], ['physical_therapy', 'Physical Therapy'],
  ['exosomes', 'Exosomes'], ['stem_cells', 'Stem Cells'],
  ['peptides', 'Peptides'], ['medications', 'Medications'],
];
const TRAVEL_COORD = [
  ['hotel_needed', 'Hotel Needed'], ['transportation_needed', 'Transportation Needed'],
  ['passport_pending', 'Passport Pending'], ['visa_pending', 'Visa Pending'],
];
const DOC_STATUS = [
  ['waivers_signed', 'Waivers Signed'], ['labs_received', 'Labs Received'],
  ['mri_received', 'MRI Received'], ['passport_received', 'Passport Received'],
  ['consent_signed', 'Consent Signed'],
];

/* ─── Reusable inline editor primitives ──────────────────────────────────── */

/*
  ⛔⛔ EVERY CONTROL IN THIS PANEL WAS ANONYMOUS — Card CONTACT-PROFILE-INTAKE.

  `Lbl` renders a <div>. A div is not a <label>, so nothing here had an
  accessible name: a screen reader announced thirty unlabelled selects, and
  `getByLabelText(/passport status/i)` could not find Passport Status.

  ⚠⚠ THAT ABSENCE WAS LOAD-BEARING IN A TEST. contactDetailNoRemountOnEdit
  asks for /passport status/i and falls back to `document.querySelector(
  'select, input')` when the query fails — which it always did. The fallback
  returns the FIRST control in the whole document, so that test was silently
  pinned to DOM ORDER, and anything rendered above the panel would have
  retargeted it without a word. Naming the controls is what makes the test
  mean what it says.

  A context, not a prop at each of the ~40 call sites: `Field` already knows
  the label, and threading it by hand is 40 chances to wire half of it.
*/
const FieldLabelContext = React.createContext('');

/** The field label a control sits under, for its accessible name. */
function useFieldLabel() { return React.useContext(FieldLabelContext); }

function Lbl({ children, hint }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#475569',
                  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>
      {children}{hint && <span style={{ marginLeft: 6, color: '#94A3B8', fontWeight: 500, textTransform: 'none' }}>· {hint}</span>}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '6px 10px', borderRadius: 6,
  border: '1px solid #CBD5E1', fontSize: 13, color: '#0F172A',
  background: '#FFFFFF', outline: 'none',
};

function EnumSelect({ value, options, onChange, allowClear = true }) {
  const aria = useFieldLabel();
  return (
    <select
      aria-label={aria}
      value={value || ''} onChange={e => onChange(e.target.value || null)}
      style={inputStyle}
    >
      {allowClear && <option value="">— not set —</option>}
      {options.map(([v, label]) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  );
}

/*
  ⛔⛔ THE TYPED INPUTS COMMIT ON BLUR, NOT ON EVERY KEYSTROKE — Card
  CRM-SCROLL-JUMP review round 1 (P64a), 9 Oct 2026.

  `onChange` still fires per keystroke, because the LOCAL copy must track the
  box instantly or typing feels broken. What changed is that onChange no longer
  reaches the network: the panel debounces it, and `onCommit` (blur) flushes.

  ⚠ `onFocus` is reported upward so the panel knows which field a human is
  working in. A refreshed contact must never overwrite the box someone is typing
  into, and "is it focused" is the cheapest true answer to that.
*/
function NumberInput({ value, min, max, onChange, onCommit, onFocus, suffix }) {
  const aria = useFieldLabel();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input type="number" aria-label={aria} value={value ?? ''} min={min} max={max}
        onFocus={onFocus} onBlur={onCommit}
        onChange={e => {
          const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
          if (v === null || (v >= min && v <= max)) onChange(v);
        }}
        style={{ ...inputStyle, width: 80 }} />
      {suffix && <span style={{ fontSize: 11, color: '#64748B' }}>{suffix}</span>}
    </div>
  );
}

function TextInput({ value, placeholder, onChange, onCommit, onFocus, multiline }) {
  const aria = useFieldLabel();
  if (multiline) {
    return (
      <textarea aria-label={aria} value={value || ''} placeholder={placeholder}
        onFocus={onFocus} onBlur={onCommit}
        onChange={e => onChange(e.target.value)} rows={2}
        style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
    );
  }
  return (
    <input type="text" aria-label={aria} value={value || ''} placeholder={placeholder}
      onFocus={onFocus} onBlur={onCommit}
      onChange={e => onChange(e.target.value)}
      style={inputStyle} />
  );
}

function DateInput({ value, onChange }) {
  // Postgres DATE comes as 'YYYY-MM-DD' or full ISO; trim to date portion
  const dateOnly = value ? String(value).slice(0, 10) : '';
  const aria = useFieldLabel();
  return (
    <input type="date" aria-label={aria} value={dateOnly}
      onChange={e => onChange(e.target.value || null)}
      style={inputStyle} />
  );
}

function BoolToggle({ value, onChange, labelTrue = 'Yes', labelFalse = 'No' }) {
  /*
    ⚠ THE GROUP CARRIES THE FIELD NAME, NOT THE BUTTONS. Three buttons each
    named "Companion Traveling" would be three elements with the same
    accessible name — getByLabelText would throw "found multiple" and the
    buttons would lose the one thing that tells them apart.
  */
  const aria = useFieldLabel();
  return (
    <div role="group" aria-label={aria} style={{ display: 'flex', gap: 4 }}>
      {[[true, labelTrue], [false, labelFalse], [null, '— not set —']].map(([v, label]) => (
        <button key={String(v)} onClick={() => onChange(v)}
          style={{
            padding: '4px 10px', borderRadius: 6, border: '1px solid #CBD5E1',
            background: value === v ? '#0EA5E9' : '#FFFFFF',
            color: value === v ? '#FFFFFF' : '#0F172A',
            fontSize: 11, fontWeight: 600, cursor: 'pointer',
          }}>
          {label}
        </button>
      ))}
    </div>
  );
}

function MultiSelectChips({ value, options, onChange }) {
  const selected = new Set(Array.isArray(value) ? value : []);
  function toggle(v) {
    const next = new Set(selected);
    next.has(v) ? next.delete(v) : next.add(v);
    onChange(Array.from(next));
  }
  const aria = useFieldLabel();
  return (
    <div role="group" aria-label={aria} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(([v, label]) => {
        const isOn = selected.has(v);
        return (
          <button key={v} onClick={() => toggle(v)}
            style={{
              padding: '4px 10px', borderRadius: 14,
              border: `1px solid ${isOn ? '#0EA5E9' : '#CBD5E1'}`,
              background: isOn ? '#E0F2FE' : '#FFFFFF',
              color: isOn ? '#0369A1' : '#475569',
              fontSize: 11, fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
            {isOn && <Check size={11} />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

function UserPicker({ value, users, onChange, placeholder = 'Select…' }) {
  const aria = useFieldLabel();
  return (
    <select aria-label={aria} value={value || ''} onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
      style={inputStyle}>
      <option value="">— {placeholder} —</option>
      {users.map(u => (
        <option key={u.id} value={u.id}>{u.display_name} ({u.role})</option>
      ))}
    </select>
  );
}

/* ─── Section wrapper ────────────────────────────────────────────────────── */

function Section({ id, defaultOpen = true, children }) {
  const palette = SECTION_PALETTE[id];
  const [open, setOpen] = useState(defaultOpen);
  const Icon = palette.icon;
  return (
    <div style={{
      background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 10,
      marginBottom: 12, overflow: 'hidden',
    }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', padding: '10px 14px', display: 'flex', alignItems: 'center',
        gap: 10, background: palette.bg, border: 'none', cursor: 'pointer',
        borderBottom: open ? `1px solid ${palette.color}33` : 'none',
      }}>
        <Icon size={16} color={palette.color} />
        <span style={{ fontSize: 13, fontWeight: 700, color: palette.color, flex: 1, textAlign: 'left' }}>
          {palette.label}
        </span>
        {open ? <ChevronDown size={16} color={palette.color} /> : <ChevronRight size={16} color={palette.color} />}
      </button>
      {open && (
        <div style={{ padding: '14px 16px', display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)', gap: '14px 16px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children, fullWidth, rightAccessory }) {
  return (
    <FieldLabelContext.Provider value={label}>
      <div style={{ gridColumn: fullWidth ? '1 / -1' : 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <Lbl hint={hint}>{label}</Lbl>
          {rightAccessory && <div style={{ marginBottom: 4 }}>{rightAccessory}</div>}
        </div>
        {children}
      </div>
    </FieldLabelContext.Provider>
  );
}

/* ─── Phase E: AI Suggest button + suggestion display ────────────────────── */

function AISuggestButton({ loading, onClick }) {
  return (
    <button onClick={onClick} disabled={loading} title="AI Suggest"
      style={{
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '2px 8px', borderRadius: 10,
        background: loading ? '#F1F5F9' : 'linear-gradient(135deg, #A855F7 0%, #2E8B98 100%)',
        color: loading ? '#94A3B8' : '#FFFFFF',
        border: 'none', fontSize: 10, fontWeight: 700, cursor: loading ? 'wait' : 'pointer',
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>
      {loading ? (
        <><RefreshCw size={10} style={{ animation: 'spin 1s linear infinite' }} /> Thinking…</>
      ) : (
        <><Sparkles size={10} /> AI Suggest</>
      )}
    </button>
  );
}

function AISuggestionBox({ suggestion, error, valueLabel, valueColor, onAccept, onDismiss }) {
  if (error) {
    return (
      <div style={{
        marginTop: 6, padding: '8px 10px', borderRadius: 8,
        background: '#FEE2E2', border: '1px solid #DC2626',
        color: '#991B1B', fontSize: 11,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
      }}>
        <span>✗ {error}</span>
        <button onClick={onDismiss} style={{
          padding: '2px 6px', border: 'none', background: 'none', cursor: 'pointer',
          color: '#991B1B',
        }}><XIcon size={11} /></button>
      </div>
    );
  }
  if (!suggestion) return null;

  const color = valueColor(suggestion);
  const confidenceColor = {
    high: '#10B981', medium: '#F59E0B', low: '#94A3B8',
  }[suggestion.confidence] || '#94A3B8';

  return (
    <div style={{
      marginTop: 6, padding: '8px 10px', borderRadius: 8,
      background: 'linear-gradient(135deg, #FAF5FF 0%, #F0FDFA 100%)',
      border: '1px solid #C4B5FD',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
        <Sparkles size={12} color="#A855F7" />
        <span style={{ fontSize: 9, fontWeight: 700, color: '#7C3AED', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          AI Suggestion
        </span>
        <span style={{
          padding: '1px 6px', borderRadius: 4, background: confidenceColor + '22',
          color: confidenceColor, fontSize: 9, fontWeight: 700, textTransform: 'uppercase',
        }}>{suggestion.confidence} conf</span>
        <div style={{ flex: 1 }} />
        <strong style={{ color, fontSize: 14, fontWeight: 800 }}>
          {valueLabel(suggestion)}
        </strong>
      </div>
      <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.4, marginBottom: 6 }}>
        {suggestion.rationale}
      </div>
      {Array.isArray(suggestion.keySignals) && suggestion.keySignals.length > 0 && (
        <div style={{ marginBottom: 6 }}>
          <div style={{ fontSize: 9, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', marginBottom: 3 }}>
            Key signals
          </div>
          <ul style={{ margin: 0, paddingLeft: 14, fontSize: 10, color: '#64748B', lineHeight: 1.5 }}>
            {suggestion.keySignals.map((s, i) => <li key={i}>{s}</li>)}
          </ul>
        </div>
      )}
      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
        <button onClick={onDismiss} style={{
          padding: '4px 10px', borderRadius: 6, border: '1px solid #CBD5E1',
          background: '#FFFFFF', color: '#475569', fontSize: 11, fontWeight: 600, cursor: 'pointer',
        }}>Dismiss</button>
        <button onClick={() => onAccept(suggestion)} style={{
          padding: '4px 10px', borderRadius: 6, border: 'none',
          background: '#2E8B98', color: '#FFFFFF', fontSize: 11, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
        }}><Check size={11} /> Accept</button>
      </div>
    </div>
  );
}

/* ─── Main panel ─────────────────────────────────────────────────────────── */

/**
 * ⛔ DEFINED AT MODULE SCOPE, NOT INSIDE THE PANEL — Card CRM-SCROLL-JUMP.
 *
 * This used to be declared inside ContactFieldsPanel's body, which makes a NEW
 * COMPONENT TYPE on every render: React cannot match the old element to the new
 * one, so it unmounts and remounts each status span every time anything in the
 * panel changes.
 *
 * ⚠ It was NOT the scroll bug — these spans hold no scroll and no focus, and
 * the measured cause was the full-page spinner in ContactDetailView. It is
 * fixed here because it is the same class of defect sitting in the same file,
 * and the next thing someone moves inside that closure might be a field.
 */
function FieldStatus({ field, saving, errors }) {
  if (saving[field]) {
    return <span style={{ fontSize: 9, color: '#94A3B8', marginLeft: 6 }}>saving…</span>;
  }
  if (errors[field]) {
    return <span style={{ fontSize: 9, color: '#DC2626', marginLeft: 6 }}>✗ {errors[field]}</span>;
  }
  return null;
}

export default function ContactFieldsPanel({ contact, onPatched }) {
  const [taxonomy, setTaxonomy] = useState({ rows: [], grouped: {} });
  const [concierges, setConcierges] = useState([]);
  const [closers, setClosers] = useState([]);   // P112
  const [savingFields, setSavingFields] = useState({});
  const [errorFields, setErrorFields] = useState({});

  // Phase E: per-field AI suggestion state. Keyed by field name → { loading, suggestion, error }
  const [aiSuggestions, setAiSuggestions] = useState({});

  const requestAiSuggestion = useCallback(async (field, apiCall) => {
    setAiSuggestions(prev => ({ ...prev, [field]: { loading: true } }));
    try {
      const r = await apiCall();
      if (!r || r.success === false) {
        setAiSuggestions(prev => ({ ...prev, [field]: { error: r?.error || 'AI suggest failed' } }));
        return;
      }
      setAiSuggestions(prev => ({ ...prev, [field]: { suggestion: r } }));
    } catch (err) {
      setAiSuggestions(prev => ({ ...prev, [field]: { error: err.message } }));
    }
  }, []);

  const dismissAiSuggestion = useCallback((field) => {
    setAiSuggestions(prev => { const n = { ...prev }; delete n[field]; return n; });
  }, []);

  // Load taxonomy + concierges once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tax, con, clo] = await Promise.all([
          window.zignagenixAPI.getConditionTaxonomy(),
          window.zignagenixAPI.listConcierges(),
          // P112. Its own endpoint, not a filter over the concierge list: the
          // two role sets are different and a shared list would silently offer
          // the wrong people the day either set changes.
          window.zignagenixAPI.listClosers(),
        ]);
        if (cancelled) return;
        if (tax && !tax.error) setTaxonomy(tax);
        if (Array.isArray(con)) setConcierges(con);
        if (Array.isArray(clo)) setClosers(clo);
      } catch (e) {
        console.warn('[ContactFieldsPanel] failed to load lookups', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Local optimistic copy of the contact so edits feel instant.
  const [local, setLocal] = useState(contact || {});

  /*
    ⛔⛔ WHY ANY OF THE BOOKKEEPING BELOW EXISTS — Card CRM-SCROLL-JUMP review
    round 1 (P64a), 9 Oct 2026.

    This panel used to be TORN DOWN on every save: ContactDetailView's
    `if (loading)` gate replaced the whole view with a spinner, so the panel
    remounted from the server's copy each time and none of the races below could
    be observed. Fixing that (the whole point of this card) left the panel
    mounted — and uncovered a second, worse bug that had been hiding behind the
    first.

    ⇒ MEASURED AT PR HEAD d620099e: typing "Maria Lopez" into Companion Name at
    a human cadence left the BOX reading "Maria Lopez" and the SERVER holding
    "Marz". Every keystroke fired a save; each save's reload came back with a new
    updated_at; the effect replaced the ENTIRE local copy with the server's
    mid-word value; the remaining keystrokes landed in a box that had been reset
    under the user's fingers. The screen looked right and the record was wrong,
    which is the failure mode nobody reports because nobody sees it.

    Three things are tracked per field, and each answers a different question:

      dirtyRef    — has the user changed this since we last sent it?
      focusRef    — is the caret in this box right now?
      inFlightRef — how many saves for this field have not come back yet?

    ⚠ inFlightRef is a COUNT, not a boolean. Two overlapping saves resolving out
    of order would otherwise have the first one to return clear the flag while
    the second is still outstanding, re-opening exactly the window this closes.
  */
  const dirtyRef = useRef({});
  const focusRef = useRef(null);
  const inFlightRef = useRef({});
  const timersRef = useRef({});
  /*
    ⚠ A monotonic per-field edit counter. A save clears `dirty` only if NO edit
    has happened since it was sent — and "since" has to be answered by something
    that cannot be stale. Comparing the committed value against localRef was the
    obvious alternative and is subtly wrong: localRef is updated by an effect, so
    a discrete save that resolves before React has re-rendered would compare
    against the PREVIOUS value, fail to clear `dirty`, and leave the field
    permanently protected — after which no server update for it would ever apply
    again. A counter has no such window.
  */
  const editSeqRef = useRef({});
  const localRef = useRef(local);
  useEffect(() => { localRef.current = local; }, [local]);

  // The contact id, for the unmount flush below (props are stale in cleanup).
  const contactIdRef = useRef(contact?.id);
  useEffect(() => { contactIdRef.current = contact?.id; }, [contact?.id]);

  /** A field the server must not overwrite: being typed in, edited, or saving. */
  const isProtected = useCallback((field) => (
    focusRef.current === field
    || dirtyRef.current[field]
    || (inFlightRef.current[field] || 0) > 0
  ), []);

  /*
    ⛔⛔ MERGE FIELD BY FIELD. NEVER `setLocal(contact)`.

    The old line was `useEffect(() => { setLocal(contact || {}); }, [...])` — a
    wholesale replacement, which is correct only if the user is guaranteed not to
    be mid-edit. That guarantee came from the remount, and the remount is gone.

    ⚠ Keyed on the contact OBJECT, not on `updated_at`. Two reloads inside the
    same millisecond produce an identical ISO string, and an effect keyed on that
    string silently does not re-run — a refresh that never lands is the mirror
    image of the bug above and just as quiet.
  */
  useEffect(() => {
    if (!contact) return;
    setLocal((prev) => {
      // A different contact entirely ⇒ nothing local is worth keeping.
      if (prev?.id !== contact.id) {
        dirtyRef.current = {};
        inFlightRef.current = {};
        editSeqRef.current = {};
        focusRef.current = null;
        return { ...contact };
      }
      const next = { ...prev };
      for (const key of Object.keys(contact)) {
        if (isProtected(key)) continue;   // the user owns this one right now
        next[key] = contact[key];
      }
      return next;
    });
  }, [contact, isProtected]);

  /** The actual network save. Everything else funnels through here. */
  const commit = useCallback(async (field, value) => {
    const sentAtSeq = editSeqRef.current[field] || 0;
    inFlightRef.current[field] = (inFlightRef.current[field] || 0) + 1;
    setSavingFields(prev => ({ ...prev, [field]: true }));
    setErrorFields(prev => { const n = { ...prev }; delete n[field]; return n; });
    try {
      const r = await window.zignagenixAPI.patchContactFields(contactIdRef.current, { [field]: value });
      if (!r || r.success === false) {
        setErrorFields(prev => ({ ...prev, [field]: r?.error || 'save failed' }));
      } else {
        /*
          ⚠ CLEAR `dirty` ONLY IF NOTHING WAS TYPED WHILE THIS WAS IN FLIGHT. If
          the user kept typing, the field is still dirty and must stay protected
          — clearing it here would hand the next reload a licence to overwrite
          the newer keystrokes, which is the whole bug this card is fixing.
        */
        if (editSeqRef.current[field] === sentAtSeq) delete dirtyRef.current[field];
        if (onPatched) onPatched(field, value);
      }
    } catch (err) {
      setErrorFields(prev => ({ ...prev, [field]: err.message }));
    } finally {
      const n = (inFlightRef.current[field] || 1) - 1;
      if (n <= 0) delete inFlightRef.current[field]; else inFlightRef.current[field] = n;
      if (!inFlightRef.current[field]) {
        setSavingFields(prev => { const m = { ...prev }; delete m[field]; return m; });
      }
    }
  }, [onPatched]);

  /*
    Discrete controls — selects, toggles, dates, chip pickers — save AT ONCE.

    ⚠ Deliberately NOT debounced. A dropdown is one decided act with no
    intermediate states worth protecting; delaying it would make the screen feel
    broken and would lose the edit if the user navigated away inside the window.
    The debounce exists for controls a human types INTO, and nothing else.
  */
  const patch = useCallback((field, value) => {
    setLocal(prev => ({ ...prev, [field]: value }));
    editSeqRef.current[field] = (editSeqRef.current[field] || 0) + 1;
    dirtyRef.current[field] = true;
    return commit(field, value);
  }, [commit]);

  /*
    Typed controls — text, multiline, number.

    700 ms of quiet, or a blur, whichever comes first. The reviewer named the
    number; the point is that it comfortably exceeds the gap between keystrokes
    of someone typing a name, so a word is saved once rather than per letter.
  */
  const TYPING_DEBOUNCE_MS = 700;
  const patchTyped = useCallback((field, value) => {
    setLocal(prev => ({ ...prev, [field]: value }));
    editSeqRef.current[field] = (editSeqRef.current[field] || 0) + 1;
    dirtyRef.current[field] = true;
    if (timersRef.current[field]) clearTimeout(timersRef.current[field]);
    timersRef.current[field] = setTimeout(() => {
      delete timersRef.current[field];
      commit(field, localRef.current?.[field]);
    }, TYPING_DEBOUNCE_MS);
  }, [commit]);

  /** Blur: stop waiting and save now, if there is anything to save. */
  const flushField = useCallback((field) => {
    if (focusRef.current === field) focusRef.current = null;
    if (timersRef.current[field]) {
      clearTimeout(timersRef.current[field]);
      delete timersRef.current[field];
    }
    if (dirtyRef.current[field]) commit(field, localRef.current?.[field]);
  }, [commit]);

  /** Props shared by every typed control, so a call site cannot wire half of it. */
  const typedProps = useCallback((field) => ({
    onChange: (v) => patchTyped(field, v),
    onCommit: () => flushField(field),
    onFocus: () => { focusRef.current = field; },
  }), [patchTyped, flushField]);

  /*
    ⛔ ON UNMOUNT, SEND WHAT IS STILL PENDING. A debounce introduces a window in
    which a typed edit exists only in this component, so navigating away inside
    it would silently discard the user's work — a NEW way to lose data, created
    by the fix for losing data. Fired directly at the API with no state updates,
    because the component is going away and setState on it would warn.
  */
  useEffect(() => {
    return () => {
      /*
        ⚠ READ THE REFS AT CLEANUP TIME, NOT AT MOUNT. Capturing
        `timersRef.current` / `dirtyRef.current` into consts at mount looks
        equivalent and is not: the contact-change branch of the sync effect
        REPLACES those objects (`dirtyRef.current = {}`), so a flush after the
        user had switched contacts would iterate a map that is no longer the
        live one — dropping the pending edit it exists to save.
      */
      const timers = timersRef.current;
      const dirty = dirtyRef.current;
      for (const field of Object.keys(timers)) {
        clearTimeout(timers[field]);
        delete timers[field];
      }
      for (const field of Object.keys(dirty)) {
        const value = localRef.current?.[field];
        try {
          window.zignagenixAPI.patchContactFields(contactIdRef.current, { [field]: value });
        } catch (e) { /* the component is gone; nothing left to tell */ }
      }
    };
  }, []);

  // Subcategory options based on currently-selected category
  const subcatOptions = useMemo(() => {
    const cat = local.condition_category;
    if (!cat) return [];
    const list = taxonomy.grouped[cat] || [];
    return list.filter(r => r.subcategory).map(r => [r.subcategory, r.label]);
  }, [local.condition_category, taxonomy]);

  if (!contact) return null;

  return (
    <div style={{ marginTop: 16 }}>
      {/* ─── Identity additions ─────────────────────────────────────────── */}
      <Section id="identity">
        <Field label="Passport Status">
          <EnumSelect value={local.passport_status} options={PASSPORT_STATUS}
            onChange={v => patch('passport_status', v)} />
          <FieldStatus field="passport_status" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Passport Expiration">
          <DateInput value={local.passport_expiration}
            onChange={v => patch('passport_expiration', v)} />
          <FieldStatus field="passport_expiration" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Companion Traveling">
          <BoolToggle value={local.companion_traveling}
            onChange={v => patch('companion_traveling', v)} />
          <FieldStatus field="companion_traveling" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Companion Name" hint="if applicable">
          {/* ⛔ TYPED ⇒ debounced + blur. See typedProps / patchTyped. */}
          <TextInput value={local.companion_name} placeholder="Spouse, parent, friend, etc."
            {...typedProps('companion_name')} />
          <FieldStatus field="companion_name" saving={savingFields} errors={errorFields} />
        </Field>
      </Section>

      {/* ─── Sales Intelligence ─────────────────────────────────────────── */}
      <Section id="sales">
        <Field label="Lead Temperature" hint="manual override">
          <EnumSelect value={local.lead_temperature} options={LEAD_TEMP}
            onChange={v => patch('lead_temperature', v)} />
          <FieldStatus field="lead_temperature" saving={savingFields} errors={errorFields} />
        </Field>
        <Field
          label="Patient Intent Score"
          hint="1 = curious, 10 = ready to book"
          rightAccessory={
            <AISuggestButton
              loading={aiSuggestions.patient_intent_score?.loading}
              onClick={() => requestAiSuggestion('patient_intent_score',
                () => window.zignagenixAPI.aiScoreIntent(contact.id))}
            />
          }
        >
          {/*
            ⛔ TYPED. Reaching 10 means passing through 1, which is itself a
            valid score — so a per-keystroke save stored 1 and the reload put 1
            back in the box before the 0 could be typed.
          */}
          <NumberInput value={local.patient_intent_score} min={1} max={10}
            {...typedProps('patient_intent_score')} suffix="/ 10" />
          <FieldStatus field="patient_intent_score" saving={savingFields} errors={errorFields} />
          <AISuggestionBox
            suggestion={aiSuggestions.patient_intent_score?.suggestion}
            error={aiSuggestions.patient_intent_score?.error}
            valueLabel={s => `${s.score} / 10`}
            valueColor={s => s.score >= 7 ? '#10B981' : s.score >= 4 ? '#F59E0B' : '#94A3B8'}
            onAccept={s => { patch('patient_intent_score', s.score); dismissAiSuggestion('patient_intent_score'); }}
            onDismiss={() => dismissAiSuggestion('patient_intent_score')}
          />
        </Field>
        <Field label="Financial Readiness">
          <EnumSelect value={local.financial_readiness} options={FINANCIAL_READINESS}
            onChange={v => patch('financial_readiness', v)} />
          <FieldStatus field="financial_readiness" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Communication Preference">
          <EnumSelect value={local.communication_preference} options={COMM_PREF}
            onChange={v => patch('communication_preference', v)} />
          <FieldStatus field="communication_preference" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Last Contact Outcome">
          <EnumSelect value={local.last_contact_outcome} options={LAST_OUTCOME}
            onChange={v => patch('last_contact_outcome', v)} />
          <FieldStatus field="last_contact_outcome" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Follow-Up Priority">
          <EnumSelect value={local.followup_priority} options={FOLLOWUP_PRIORITY}
            onChange={v => patch('followup_priority', v)} />
          <FieldStatus field="followup_priority" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Decision Maker">
          <EnumSelect value={local.decision_maker_status} options={DECISION_MAKER}
            onChange={v => patch('decision_maker_status', v)} />
          <FieldStatus field="decision_maker_status" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Medical Urgency">
          <EnumSelect value={local.medical_urgency} options={MEDICAL_URGENCY}
            onChange={v => patch('medical_urgency', v)} />
          <FieldStatus field="medical_urgency" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Confidence in Clinic">
          <EnumSelect value={local.confidence_level} options={CONFIDENCE}
            onChange={v => patch('confidence_level', v)} />
          <FieldStatus field="confidence_level" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Competitors Mentioned" fullWidth>
          <MultiSelectChips value={local.competitor_mentioned} options={COMPETITORS}
            onChange={v => patch('competitor_mentioned', v)} />
          <FieldStatus field="competitor_mentioned" saving={savingFields} errors={errorFields} />
        </Field>
      </Section>

      {/* ─── Medical Intelligence ───────────────────────────────────────── */}
      <Section id="medical">
        <Field label="Condition Category">
          <EnumSelect value={local.condition_category}
            options={Object.keys(taxonomy.grouped).map(c => [c, c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())])}
            onChange={v => {
              patch('condition_category', v);
              // Reset subcategory if the category changed
              if (v !== local.condition_category) patch('condition_subcategory', null);
            }} />
          <FieldStatus field="condition_category" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Condition Subcategory" hint={local.condition_category ? '' : 'pick a category first'}>
          {subcatOptions.length > 0 ? (
            <EnumSelect value={local.condition_subcategory} options={subcatOptions}
              onChange={v => patch('condition_subcategory', v)} />
          ) : (
            /* ⛔ TYPED (this is the free-text fallback; the select arm above
               is a discrete choice and stays immediate). */
            <TextInput value={local.condition_subcategory} placeholder="—"
              {...typedProps('condition_subcategory')} />
          )}
          <FieldStatus field="condition_subcategory" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Severity">
          <EnumSelect value={local.severity_level} options={SEVERITY}
            onChange={v => patch('severity_level', v)} />
          <FieldStatus field="severity_level" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Mobility Status">
          <EnumSelect value={local.mobility_status} options={MOBILITY}
            onChange={v => patch('mobility_status', v)} />
          <FieldStatus field="mobility_status" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="MRI / X-Ray Uploaded">
          <BoolToggle value={local.mri_xray_uploaded}
            onChange={v => patch('mri_xray_uploaded', v)} />
          <FieldStatus field="mri_xray_uploaded" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Candidate Status">
          <EnumSelect value={local.candidate_status} options={CANDIDATE}
            onChange={v => patch('candidate_status', v)} />
          <FieldStatus field="candidate_status" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Previous Treatments Tried" fullWidth>
          <MultiSelectChips value={local.previous_treatments_tried} options={PRIOR_TREATMENTS}
            onChange={v => patch('previous_treatments_tried', v)} />
          <FieldStatus field="previous_treatments_tried" saving={savingFields} errors={errorFields} />
        </Field>
      </Section>

      {/* ─── Operational ────────────────────────────────────────────────── */}
      <Section id="operational">
        <Field label="Assigned Concierge">
          <UserPicker value={local.assigned_concierge_id} users={concierges}
            onChange={v => patch('assigned_concierge_id', v)} />
          <FieldStatus field="assigned_concierge_id" saving={savingFields} errors={errorFields} />
        </Field>
        {/*
          P112 — staff feedback E2 (Luisa). A patient is worked by TWO people:
          the concierge above handles travel logistics, the closer owns the
          financial call. Same autosave path, same FieldStatus, same patch
          allowlist — deliberately indistinguishable in behaviour from the
          field beside it, because a second assignment that saved differently
          from the first is how staff stop trusting both.
        */}
        <Field label="Assigned Closer">
          <UserPicker value={local.assigned_closer_id} users={closers}
            onChange={v => patch('assigned_closer_id', v)} />
          <FieldStatus field="assigned_closer_id" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Clinic Location">
          <EnumSelect value={local.clinic_location} options={CLINIC}
            onChange={v => patch('clinic_location', v)} />
          <FieldStatus field="clinic_location" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Payment Status">
          <EnumSelect value={local.payment_status} options={PAYMENT}
            onChange={v => patch('payment_status', v)} />
          <FieldStatus field="payment_status" saving={savingFields} errors={errorFields} />
        </Field>
        <Field
          label="No-Show Risk"
          rightAccessory={
            <AISuggestButton
              loading={aiSuggestions.no_show_risk?.loading}
              onClick={() => requestAiSuggestion('no_show_risk',
                () => window.zignagenixAPI.aiScoreNoShowRisk(contact.id))}
            />
          }
        >
          <EnumSelect value={local.no_show_risk} options={NO_SHOW_RISK}
            onChange={v => patch('no_show_risk', v)} />
          <FieldStatus field="no_show_risk" saving={savingFields} errors={errorFields} />
          <AISuggestionBox
            suggestion={aiSuggestions.no_show_risk?.suggestion}
            error={aiSuggestions.no_show_risk?.error}
            valueLabel={s => s.risk?.toUpperCase()}
            valueColor={s => s.risk === 'high' ? '#DC2626' : s.risk === 'medium' ? '#F59E0B' : '#10B981'}
            onAccept={s => { patch('no_show_risk', s.risk); dismissAiSuggestion('no_show_risk'); }}
            onDismiss={() => dismissAiSuggestion('no_show_risk')}
          />
        </Field>
        <Field label="Travel Coordination" fullWidth>
          <MultiSelectChips value={local.travel_coordination_status} options={TRAVEL_COORD}
            onChange={v => patch('travel_coordination_status', v)} />
          <FieldStatus field="travel_coordination_status" saving={savingFields} errors={errorFields} />
        </Field>
        <Field label="Documents Received" fullWidth>
          <MultiSelectChips value={local.document_status} options={DOC_STATUS}
            onChange={v => patch('document_status', v)} />
          <FieldStatus field="document_status" saving={savingFields} errors={errorFields} />
        </Field>
      </Section>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────────
 * NextActionPill — compact header pill that surfaces the contact's next
 * action and due date. Click to edit inline. Always visible at the top of
 * the contact view.
 * ────────────────────────────────────────────────────────────────────────── */

export function NextActionPill({ contact, onPatched }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(contact?.next_action || '');
  const [due, setDue] = useState(contact?.next_action_due ? String(contact.next_action_due).slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setText(contact?.next_action || '');
    setDue(contact?.next_action_due ? String(contact.next_action_due).slice(0, 10) : '');
  }, [contact?.id, contact?.updated_at]);

  if (!contact) return null;

  async function save() {
    setSaving(true); setError(null);
    try {
      const r = await window.zignagenixAPI.patchContactFields(contact.id, {
        next_action: text || null,
        next_action_due: due || null,
      });
      if (!r || r.success === false) {
        setError(r?.error || 'save failed');
      } else {
        if (onPatched) {
          onPatched('next_action', text);
          onPatched('next_action_due', due);
        }
        setEditing(false);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  // ── Display mode ──
  if (!editing) {
    const hasAction = !!contact.next_action;
    const overdue = contact.next_action_due && new Date(contact.next_action_due) < new Date(new Date().toDateString());
    return (
      <button onClick={() => setEditing(true)} style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px', borderRadius: 999,
        background: hasAction ? (overdue ? '#FEE2E2' : '#DBEAFE') : '#F1F5F9',
        border: `1px solid ${hasAction ? (overdue ? '#DC2626' : '#3B82F6') : '#CBD5E1'}`,
        cursor: 'pointer',
        color: hasAction ? (overdue ? '#991B1B' : '#1E40AF') : '#64748B',
        fontSize: 12, fontWeight: 600,
      }}>
        {overdue && <AlertTriangle size={12} />}
        <span style={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Next Action:
        </span>
        <span>{hasAction ? contact.next_action : '— set next action —'}</span>
        {contact.next_action_due && (
          <span style={{ fontSize: 10, opacity: 0.8 }}>
            · {overdue ? 'OVERDUE ' : 'due '}{new Date(contact.next_action_due).toLocaleDateString()}
          </span>
        )}
      </button>
    );
  }

  // ── Edit mode ──
  return (
    <div style={{
      display: 'flex', gap: 6, alignItems: 'center',
      padding: 6, background: '#F8FAFC', border: '1px solid #CBD5E1',
      borderRadius: 8,
    }}>
      <input type="text" value={text} placeholder="e.g. Call today, Send quote, Awaiting MRI"
        onChange={e => setText(e.target.value)}
        style={{ ...inputStyle, width: 240 }} autoFocus />
      <input type="date" value={due} onChange={e => setDue(e.target.value)}
        style={{ ...inputStyle, width: 140 }} />
      <button onClick={save} disabled={saving} style={{
        padding: '5px 10px', borderRadius: 6, border: 'none',
        background: '#2E8B98', color: '#FFF', fontSize: 11, fontWeight: 600,
        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <Check size={12} /> {saving ? 'Saving…' : 'Save'}
      </button>
      <button onClick={() => setEditing(false)} style={{
        padding: '5px 8px', borderRadius: 6, border: '1px solid #CBD5E1',
        background: '#FFF', color: '#475569', fontSize: 11, cursor: 'pointer',
      }}>
        <XIcon size={12} />
      </button>
      {error && <span style={{ fontSize: 10, color: '#DC2626' }}>✗ {error}</span>}
    </div>
  );
}
